export type DependentOnResult<T> =
  | { isLoading: true; value?: undefined }
  | { isLoading: false; value: T }

/**
 * Wraps a function that only makes sense if the first argument is true.
 * Returns `isLoading` and `value`.
 * This leverages TypeScript's discriminated unions to ensure type safety.
 * If you check value before isLoading has been narrowed to true, you will get a type error.
 */
export function dependentOn<T>(
  isLoaded: boolean,
  computeFn: () => T
): DependentOnResult<T> {
  if (!isLoaded) {
    return { isLoading: true }
  }
  const result = computeFn()
  return { isLoading: false, value: result as T }
}

export function chainedDependentOn<U, T>(
  source: DependentOnResult<U>,
  computeFn: (value: U) => T
): DependentOnResult<T> {
  if (source.isLoading) {
    return { isLoading: true }
  }
  const result = computeFn(source.value)
  return { isLoading: false, value: result }
}
